import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { formatCpf } from "@/lib/utils/cpf-mask";
import { invalidateAppointmentUiCache } from "@/lib/react-query/invalidateAppointmentUiCache";
import { invalidateReportsCache } from "@/lib/react-query/invalidateReportsCache";
import { invalidateSalesCache } from "@/lib/react-query/invalidateSalesCache";
import { reconcileAppointmentFinancial } from "@/lib/services/appointment-financial-reconciliation";
import type { Database } from "@/integrations/supabase/types";
import { useTranslation } from "react-i18next";
import { labelItemPets } from "@/lib/sales/pet-split";

type SaleStatus = Database["public"]["Enums"]["sale_status"];
type PaymentMethod = Database["public"]["Enums"]["payment_method"];

export interface SaleItemRow {
  id: string;
  sale_id: string;
  item_id: string | null;
  item_name: string;
  quantity: number;
  unit_price: number;
  discount_amount: number | null;
  line_total: number;
  item?: {
    type?: Database["public"]["Enums"]["item_type"] | null;
  } | null;
  grooming_item?: {
    type?: string | null;
  } | null;
  /** Nome do pet do item; só vem preenchido quando a venda é de mais de um pet. */
  pet_name?: string | null;
}

export interface SalePaymentRow {
  id: string;
  sale_id: string;
  method: string;
  amount: number;
  received_at?: string | null;
  reference?: string | null;
}

export interface SaleDetail {
  id: string;
  sale_number: string;
  created_at: string | null;
  total: number | null;
  status: string | null;
  subtotal: number | null;
  discount_amount: number | null;
  discount_percentage: number | null;
  appointment_id: string | null;
  payer_display: string | null;
  created_by_user_name: string | null;
  /** When sale is linked to an appointment, names of all patients from that appointment. */
  appointment_pet_names?: string[];
  items: SaleItemRow[];
  payments: SalePaymentRow[];
  sale_payments?: SalePaymentRow[];
}

export function useSaleDetail(saleId: string | null) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation(["erp", "admin"]);

  const query = useQuery({
    queryKey: ["sale-detail", saleId],
    queryFn: async (): Promise<SaleDetail | null> => {
      if (!saleId) return null;
      const { data: sale, error: saleError } = await supabase
        .from("sales")
        .select("*")
        .eq("id", saleId)
        .single();

      if (saleError || !sale) throw saleError ?? new Error(t("erp:phase8.saleDetail.errors.saleNotFound"));

      let payer_display: string | null = null;
      if (sale.owner_id || sale.pet_id) {
        let ownerName: string | null = null;
        let petName: string | null = null;
        if (sale.owner_id) {
          const { data: owner } = await supabase
            .from("owners")
            .select("name")
            .eq("id", sale.owner_id)
            .single();
          ownerName = owner?.name ?? null;
        }
        if (sale.pet_id) {
          const { data: pet } = await supabase
            .from("pets")
            .select("name")
            .eq("id", sale.pet_id)
            .single();
          petName = pet?.name ?? null;
        }
        if (ownerName && petName) payer_display = t("erp:phase8.saleDetail.payerOwnerAndPatient", { ownerName, petName });
        else if (ownerName) payer_display = ownerName;
        else if (petName) payer_display = t("erp:phase8.saleDetail.payerPatientOnly", { petName });
      } else if (sale.buyer_cpf) {
        const digits = String(sale.buyer_cpf).replace(/\D/g, "");
        payer_display = digits.length >= 11
          ? t("erp:phase8.saleDetail.payerCpf", { cpf: formatCpf(String(sale.buyer_cpf)) })
          : t("erp:phase8.saleDetail.payerCpf", { cpf: sale.buyer_cpf });
      }
      if (payer_display === null) payer_display = t("admin:emptyDash");

      let created_by_user_name: string | null = null;
      if (sale.created_by) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("id", sale.created_by)
          .single();
        created_by_user_name = profile?.full_name ?? null;
      }

      const { data: items, error: itemsError } = await supabase
        .from("sale_items")
        .select("id, sale_id, item_id, item_name, quantity, unit_price, discount_amount, line_total, item:items(type)")
        .eq("sale_id", saleId)
        .order("created_at", { ascending: true });

      if (itemsError) throw itemsError;

      const { data: payments, error: paymentsError } = await supabase
        .from("sale_payments")
        .select("id, sale_id, method, amount, received_at, reference")
        .eq("sale_id", saleId);

      if (paymentsError) throw paymentsError;

      let appointment_pet_names: string[] | undefined;
      if (sale.appointment_id) {
        const { data: apt, error: aptError } = await supabase
          .from("appointments")
          .select("appointment_pets(pet:pets(id, name))")
          .eq("id", sale.appointment_id)
          .single();
        if (aptError) throw aptError;
        const appointmentPets =
          ((apt as unknown as { appointment_pets?: Array<{ pet?: { id: string; name?: string } | null }> })
            ?.appointment_pets ?? []);
        const pets = appointmentPets
          .map((ap: { pet?: { id: string; name?: string } | null }) => ap?.pet)
          .filter((p: { id: string; name?: string } | null | undefined): p is { id: string; name?: string } => !!p?.id);
        appointment_pet_names = pets.map((p) => p.name ?? "").filter(Boolean);
      }

      // venda de mais de um pet: cada item mostra de qual pet é (sale_items.pet_id não está no types.ts gerado)
      const itemPetLabels = new Map<string, string>();
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: itemPets } = await (supabase as any).from("sale_items").select("id, pet_id").eq("sale_id", saleId);
        const petIdByItemId = new Map<string, string | null>(
          ((itemPets ?? []) as Array<{ id: string; pet_id: string | null }>).map((row) => [row.id, row.pet_id]),
        );
        const petIds = Array.from(new Set([sale.pet_id, ...petIdByItemId.values()])).filter((id): id is string => Boolean(id));
        if (petIds.length > 1) {
          const { data: petRows } = await supabase.from("pets").select("id, name").in("id", petIds);
          const petNames = new Map((petRows ?? []).map((row) => [row.id, row.name ?? ""] as [string, string]));
          const labels = labelItemPets(
            (items ?? []).map((item) => ({ id: item.id, petId: petIdByItemId.get(item.id) ?? null })),
            sale.pet_id ?? null,
            petNames,
          );
          labels.forEach((name, itemId) => itemPetLabels.set(itemId, name));
        }
      } catch {
        // o rótulo do pet é um complemento: se falhar, o detalhe da venda abre sem ele
      }

      return {
        ...sale,
        payer_display,
        created_by_user_name,
        appointment_pet_names,
        items: ((items ?? []) as SaleItemRow[]).map((item) => ({ ...item, pet_name: itemPetLabels.get(item.id) ?? null })),
        payments: (payments ?? []) as SalePaymentRow[],
      } as SaleDetail;
    },
    enabled: !!saleId,
  });

  const invalidateSaleRelatedCaches = (result: { saleId: string; appointmentId: string | null }) => {
    queryClient.invalidateQueries({ queryKey: ["sale-detail", result.saleId] });
    invalidateSalesCache(queryClient, { appointmentId: result.appointmentId });
    invalidateReportsCache(queryClient);
    queryClient.invalidateQueries({ queryKey: ["financial-summary"] });
    queryClient.invalidateQueries({ queryKey: ["patient-financial-summary"] });

    if (result.appointmentId) {
      invalidateAppointmentUiCache(queryClient, { appointmentId: result.appointmentId, clearLists: true });
    }
  };

  /**
   * Adjusts product_stock for a PRODUTO item. delta > 0 restores, delta < 0 decrements.
   * Silently skips if no stock row exists or item is not a PRODUTO.
   */
  const adjustProductStock = async (clinicId: string, itemId: string, delta: number) => {
    if (delta === 0) return;
    const { data: itemRow } = await supabase.from("items").select("type").eq("id", itemId).maybeSingle();
    if (itemRow?.type !== "PRODUTO") return;

    const { data: stock } = await supabase
      .from("product_stock")
      .select("id, quantity")
      .eq("clinic_id", clinicId)
      .eq("item_id", itemId)
      .maybeSingle();
    if (!stock) return;

    const newQty = Math.max(0, (stock.quantity ?? 0) + delta);
    await supabase.from("product_stock").update({ quantity: newQty }).eq("id", stock.id);
  };

  const reconcileLinkedAppointment = async (linkedSaleId: string): Promise<string | null> => {
    const { data: sale, error } = await supabase
      .from("sales")
      .select("appointment_id")
      .eq("id", linkedSaleId)
      .maybeSingle();

    if (error) throw error;

    const appointmentId = sale?.appointment_id ?? null;
    if (appointmentId) {
      await reconcileAppointmentFinancial(appointmentId);
    }

    return appointmentId;
  };

  const updateSaleStatus = useMutation({
    mutationFn: async ({ saleId: id, status }: { saleId: string; status: SaleStatus }) => {
      const { data: currentSale } = await supabase
        .from("sales")
        .select("status, clinic_id, appointment_id")
        .eq("id", id)
        .maybeSingle();

      const { error } = await supabase
        .from("sales")
        .update({ status, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;

      const isCancelling = currentSale?.status !== "CANCELADA" && currentSale?.status !== "DEVOLVIDA" && (status === "CANCELADA" || status === "DEVOLVIDA");

      // Restore stock when cancelling an active sale (best-effort, don't block invalidation).
      if (isCancelling && currentSale?.clinic_id) {
        try {
          const { data: saleItems } = await supabase
            .from("sale_items")
            .select("item_id, quantity")
            .eq("sale_id", id);
          for (const si of saleItems ?? []) {
            if (si.item_id) await adjustProductStock(currentSale.clinic_id, si.item_id, si.quantity ?? 0);
          }
        } catch (stockErr) {
          console.error("Stock restore failed on cancel:", stockErr);
        }
      }

      // appointment_financial is reverted by the DB trigger on sales.status update.
      // Call reconcile only for non-cancel transitions (e.g. DEVOLVIDA → PAGA) to keep
      // the financial status in sync with active sales.
      const appointmentId = currentSale?.appointment_id ?? null;
      if (appointmentId && !isCancelling) {
        try {
          await reconcileAppointmentFinancial(appointmentId);
        } catch (reconcileErr) {
          console.error("Appointment financial reconcile failed:", reconcileErr);
        }
      }

      return { saleId: id, appointmentId };
    },
    onSuccess: (result) => {
      invalidateSaleRelatedCaches(result);
      queryClient.invalidateQueries({ queryKey: ["product-stock"] });
      queryClient.invalidateQueries({ queryKey: ["stock-movements"] });
    },
    onError: (err: Error) => {
      toast({ title: t("erp:phase8.saleDetail.statusUpdateErrorTitle"), description: err.message, variant: "destructive" });
    },
  });

  const addSalePayment = useMutation({
    mutationFn: async ({
      saleId: id,
      method,
      amount,
      received_at,
      reference,
    }: {
      saleId: string;
      method: PaymentMethod;
      amount: number;
      received_at?: string | null;
      reference?: string | null;
    }) => {
      const { error } = await supabase.from("sale_payments").insert({
        sale_id: id,
        method,
        amount,
        received_at: received_at ?? null,
        reference: reference ?? null,
      });
      if (error) throw error;
      const appointmentId = await reconcileLinkedAppointment(id);
      return { saleId: id, appointmentId };
    },
    onSuccess: (result) => {
      invalidateSaleRelatedCaches(result);
    },
    onError: (err: Error) => {
      toast({ title: t("erp:phase8.saleDetail.paymentAddErrorTitle"), description: err.message, variant: "destructive" });
    },
  });

  const updateSalePayment = useMutation({
    mutationFn: async ({
      paymentId,
      method,
      amount,
      received_at,
      reference,
    }: {
      paymentId: string;
      method: PaymentMethod;
      amount: number;
      received_at?: string | null;
      reference?: string | null;
    }) => {
      const { data: payment, error: paymentLoadError } = await supabase
        .from("sale_payments")
        .select("sale_id")
        .eq("id", paymentId)
        .single();
      if (paymentLoadError) throw paymentLoadError;

      const { error } = await supabase
        .from("sale_payments")
        .update({
          method,
          amount,
          received_at: received_at ?? null,
          reference: reference ?? null,
        })
        .eq("id", paymentId);
      if (error) throw error;
      const appointmentId = await reconcileLinkedAppointment(payment.sale_id);
      return { saleId: payment.sale_id, appointmentId };
    },
    onSuccess: (result) => {
      invalidateSaleRelatedCaches(result);
    },
    onError: (err: Error) => {
      toast({ title: t("erp:phase8.saleDetail.paymentUpdateErrorTitle"), description: err.message, variant: "destructive" });
    },
  });

  const deleteSalePayment = useMutation({
    mutationFn: async (paymentId: string) => {
      const { data: payment, error: paymentLoadError } = await supabase
        .from("sale_payments")
        .select("sale_id")
        .eq("id", paymentId)
        .single();
      if (paymentLoadError) throw paymentLoadError;

      const { error } = await supabase.from("sale_payments").delete().eq("id", paymentId);
      if (error) throw error;
      const appointmentId = await reconcileLinkedAppointment(payment.sale_id);
      return { saleId: payment.sale_id, appointmentId };
    },
    onSuccess: (result) => {
      invalidateSaleRelatedCaches(result);
    },
    onError: (err: Error) => {
      toast({ title: t("erp:phase8.saleDetail.paymentDeleteErrorTitle"), description: err.message, variant: "destructive" });
    },
  });

  const updateSaleTotals = useMutation({
    mutationFn: async ({
      saleId: id,
      subtotal,
      discountAmount,
      total,
    }: {
      saleId: string;
      subtotal: number;
      discountAmount: number;
      total: number;
    }) => {
      const { error } = await supabase
        .from("sales")
        .update({
          subtotal,
          discount_amount: discountAmount,
          total,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);
      if (error) throw error;
      const appointmentId = await reconcileLinkedAppointment(id);
      return { saleId: id, appointmentId };
    },
    onSuccess: (result) => {
      invalidateSaleRelatedCaches(result);
      toast({ title: t("erp:phase8.saleDetail.saleUpdatedTitle"), description: t("erp:phase8.saleDetail.saleUpdatedDescription") });
    },
    onError: (err: Error) => {
      toast({ title: t("erp:phase8.saleDetail.saleUpdateErrorTitle"), description: err.message, variant: "destructive" });
    },
  });

  const updateSaleItem = useMutation({
    mutationFn: async ({
      itemId,
      quantity,
      unitPrice,
      discountAmount,
      lineTotal,
    }: {
      itemId: string;
      quantity: number;
      unitPrice: number;
      discountAmount: number;
      lineTotal: number;
    }) => {
      const { data: item, error: itemLoadError } = await supabase
        .from("sale_items")
        .select("sale_id, item_id, quantity")
        .eq("id", itemId)
        .single();
      if (itemLoadError) throw itemLoadError;

      const { data: saleRow } = await supabase.from("sales").select("clinic_id").eq("id", item.sale_id).maybeSingle();

      const { error } = await supabase
        .from("sale_items")
        .update({
          quantity,
          unit_price: unitPrice,
          discount_amount: discountAmount,
          line_total: lineTotal,
        })
        .eq("id", itemId);
      if (error) throw error;

      // Adjust stock by the delta (old qty - new qty restores, new qty - old qty decrements).
      if (saleRow?.clinic_id && item.item_id) {
        const delta = (item.quantity ?? 0) - quantity;
        await adjustProductStock(saleRow.clinic_id, item.item_id, delta);
      }

      const appointmentId = await reconcileLinkedAppointment(item.sale_id);
      return { saleId: item.sale_id, appointmentId };
    },
    onSuccess: (result) => {
      invalidateSaleRelatedCaches(result);
    },
    onError: (err: Error) => {
      toast({ title: t("erp:phase8.saleDetail.itemUpdateErrorTitle"), description: err.message, variant: "destructive" });
    },
  });

  const deleteSaleItem = useMutation({
    mutationFn: async (itemId: string) => {
      const { data: item, error: itemLoadError } = await supabase
        .from("sale_items")
        .select("sale_id, item_id, quantity")
        .eq("id", itemId)
        .single();
      if (itemLoadError) throw itemLoadError;

      const { data: saleRow } = await supabase.from("sales").select("clinic_id").eq("id", item.sale_id).maybeSingle();

      const { error } = await supabase.from("sale_items").delete().eq("id", itemId);
      if (error) throw error;

      // Restore stock when removing an item from a sale.
      if (saleRow?.clinic_id && item.item_id) {
        await adjustProductStock(saleRow.clinic_id, item.item_id, item.quantity ?? 0);
      }

      const appointmentId = await reconcileLinkedAppointment(item.sale_id);
      return { saleId: item.sale_id, appointmentId };
    },
    onSuccess: (result) => {
      invalidateSaleRelatedCaches(result);
      toast({ title: t("erp:phase8.saleDetail.itemDeletedTitle") });
    },
    onError: (err: Error) => {
      toast({ title: t("erp:phase8.saleDetail.itemDeleteErrorTitle"), description: err.message, variant: "destructive" });
    },
  });

  const addSaleItem = useMutation({
    mutationFn: async ({
      saleId,
      itemId,
      itemName,
      quantity,
      unitPrice,
      lineTotal,
    }: {
      saleId: string;
      itemId: string;
      itemName: string;
      quantity: number;
      unitPrice: number;
      lineTotal: number;
    }) => {
      const { data: saleRow } = await supabase.from("sales").select("clinic_id").eq("id", saleId).maybeSingle();

      const { error } = await supabase.from("sale_items").insert({
        sale_id: saleId,
        item_id: itemId,
        item_name: itemName,
        quantity,
        unit_price: unitPrice,
        discount_amount: 0,
        line_total: lineTotal,
      });
      if (error) throw error;

      if (saleRow?.clinic_id) {
        await adjustProductStock(saleRow.clinic_id, itemId, -quantity);
      }

      const appointmentId = await reconcileLinkedAppointment(saleId);
      return { saleId, appointmentId };
    },
    onSuccess: (result) => {
      invalidateSaleRelatedCaches(result);
      toast({ title: t("erp:phase8.saleDetail.itemAddedTitle") });
    },
    onError: (err: Error) => {
      toast({ title: t("erp:phase8.saleDetail.itemAddErrorTitle"), description: err.message, variant: "destructive" });
    },
  });

  const recalcAndSaveTotals = useMutation({
    mutationFn: async ({ saleId, discountAmount }: { saleId: string; discountAmount: number }) => {
      const { data: items, error: itemsError } = await supabase
        .from("sale_items")
        .select("line_total")
        .eq("sale_id", saleId);
      if (itemsError) throw itemsError;
      const subtotal = (items ?? []).reduce((sum, i) => sum + Number(i.line_total), 0);
      const total = Math.max(0, subtotal - discountAmount);
      const { error } = await supabase
        .from("sales")
        .update({ subtotal, discount_amount: discountAmount, total, updated_at: new Date().toISOString() })
        .eq("id", saleId);
      if (error) throw error;
      const appointmentId = await reconcileLinkedAppointment(saleId);
      return { saleId, appointmentId };
    },
    onSuccess: (result) => {
      invalidateSaleRelatedCaches(result);
      toast({ title: t("erp:phase8.saleDetail.totalsUpdatedTitle") });
    },
    onError: (err: Error) => {
      toast({ title: t("erp:phase8.saleDetail.totalsUpdateErrorTitle"), description: err.message, variant: "destructive" });
    },
  });

  return {
    ...query,
    updateSaleStatus: updateSaleStatus.mutateAsync,
    addSalePayment: addSalePayment.mutateAsync,
    updateSalePayment: updateSalePayment.mutateAsync,
    deleteSalePayment: deleteSalePayment.mutateAsync,
    updateSaleTotals: updateSaleTotals.mutateAsync,
    updateSaleItem: updateSaleItem.mutateAsync,
    deleteSaleItem: deleteSaleItem.mutateAsync,
    addSaleItem: addSaleItem.mutateAsync,
    recalcAndSaveTotals: recalcAndSaveTotals.mutateAsync,
    isUpdating:
      updateSaleStatus.isPending ||
      addSalePayment.isPending ||
      updateSalePayment.isPending ||
      deleteSalePayment.isPending ||
      updateSaleTotals.isPending ||
      updateSaleItem.isPending ||
      deleteSaleItem.isPending ||
      addSaleItem.isPending ||
      recalcAndSaveTotals.isPending,
  };
}

/** Sale and sale_items for an appointment, used in appointment financial detail. */
export function useSaleByAppointment(appointmentId: string | null) {
  return useQuery({
    queryKey: ["sale-by-appointment", appointmentId],
    queryFn: async (): Promise<SaleDetail | null> => {
      if (!appointmentId) return null;
      const { data: sale, error: saleError } = await supabase
        .from("sales")
        .select("*, sale_payments(id, sale_id, method, amount, received_at, reference)")
        .eq("appointment_id", appointmentId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (saleError) throw saleError;
      if (!sale) return null;

      const { data: items, error: itemsError } = await supabase
        .from("sale_items")
        .select("*, item:items(type), grooming_item:grooming_services_view(type)")
        .eq("sale_id", sale.id)
        .order("created_at", { ascending: true });

      if (itemsError) throw itemsError;

      return {
        ...sale,
        payer_display: null,
        created_by_user_name: null,
        items: (items ?? []) as SaleItemRow[],
        payments: [],
        sale_payments: (sale.sale_payments ?? []) as SalePaymentRow[],
      } as SaleDetail;
    },
    enabled: !!appointmentId,
  });
}
